import React from 'react';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core';
import {
  GetFunnelFormOptions_vm_activityRoles as ApplicantRoleOption,
  GetFunnelFormOptions_vm_programs as ApplicantProgramOption,
} from '../../admin-console/admin-console-content-pages/admin-console-funnels/__generated__/GetFunnelFormOptions';

export interface ApplicantPlacement {
  programIds: string[];
  activityIds: string[];
  activityRoleIds: string[];
}

export const APPLICANT_PLACEMENT_MAPPING_KEYS: Record<keyof ApplicantPlacement, string> = {
  programIds: 'applicant.programIds',
  activityIds: 'applicant.activityIds',
  activityRoleIds: 'applicant.activityRoleIds',
};

const splitIds = (value: string | undefined): string[] => (value ? value.split(',').filter(Boolean) : []);

export const readApplicantPlacement = (fieldMappings: Record<string, string>): ApplicantPlacement => {
  const programIds = splitIds(fieldMappings[APPLICANT_PLACEMENT_MAPPING_KEYS.programIds]);
  const legacyProgramId = fieldMappings['applicant.programId'];
  return {
    programIds:
      legacyProgramId && !programIds.includes(legacyProgramId) ? [...programIds, legacyProgramId] : programIds,
    activityIds: splitIds(fieldMappings[APPLICANT_PLACEMENT_MAPPING_KEYS.activityIds]),
    activityRoleIds: splitIds(fieldMappings[APPLICANT_PLACEMENT_MAPPING_KEYS.activityRoleIds]),
  };
};

export const writeApplicantPlacement = (
  fieldMappings: Record<string, string>,
  placement: ApplicantPlacement
): Record<string, string> => {
  const rest = { ...fieldMappings };
  delete rest['applicant.programId'];
  return {
    ...rest,
    [APPLICANT_PLACEMENT_MAPPING_KEYS.programIds]: placement.programIds.join(','),
    [APPLICANT_PLACEMENT_MAPPING_KEYS.activityIds]: placement.activityIds.join(','),
    [APPLICANT_PLACEMENT_MAPPING_KEYS.activityRoleIds]: placement.activityRoleIds.join(','),
  };
};

interface ApplicantPlacementFieldsProps {
  programs: ApplicantProgramOption[];
  roles: ApplicantRoleOption[];
  value: ApplicantPlacement;
  onChange: (value: ApplicantPlacement) => void;
}

export const ApplicantPlacementFields: React.FC<ApplicantPlacementFieldsProps> = ({
  programs,
  roles,
  value,
  onChange,
}) => {
  const availableActivities = programs
    .filter((program) => value.programIds.includes(program.programId))
    .flatMap((program) => program.activities);

  const names = new Map<string, string>([
    ...programs.map((p): [string, string] => [p.programId, p.name]),
    ...availableActivities.map((a): [string, string] => [a.activityId, a.name]),
    ...roles.map((r): [string, string] => [r.activityRoleId, r.name]),
  ]);
  const namesFor = (ids: string[]) =>
    ids
      .map((id) => names.get(id))
      .filter(Boolean)
      .join(', ');

  const handleProgramsChange = (programIds: string[]) => {
    const validActivityIds = programs
      .filter((program) => programIds.includes(program.programId))
      .flatMap((program) => program.activities.map((activity) => activity.activityId));
    onChange({
      ...value,
      programIds,
      activityIds: value.activityIds.filter((id) => validActivityIds.includes(id)),
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth variant='outlined' size='small'>
          <InputLabel shrink>Programs</InputLabel>
          <Select
            multiple
            displayEmpty
            value={value.programIds}
            onChange={(e) => handleProgramsChange(e.target.value as string[])}
            input={<OutlinedInput notched label='Programs' />}
            renderValue={(selected) =>
              (selected as string[]).length === 0 ? 'No programs' : namesFor(selected as string[])
            }
          >
            {programs.map((program) => (
              <MenuItem key={program.programId} value={program.programId}>
                <ListItemIcon>
                  <Checkbox color='primary' checked={value.programIds.includes(program.programId)} />
                </ListItemIcon>
                <ListItemText primary={program.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant='outlined' size='small' disabled={value.programIds.length === 0}>
          <InputLabel shrink>Activity applications</InputLabel>
          <Select
            multiple
            displayEmpty
            value={value.activityIds}
            onChange={(e) => onChange({ ...value, activityIds: e.target.value as string[] })}
            input={<OutlinedInput notched label='Activity applications' />}
            renderValue={(selected) => {
              if (value.programIds.length === 0) return 'Select programs first';
              if ((selected as string[]).length === 0) return 'No activities';
              return namesFor(selected as string[]);
            }}
          >
            {availableActivities.map((activity) => (
              <MenuItem key={activity.activityId} value={activity.activityId}>
                <ListItemIcon>
                  <Checkbox color='primary' checked={value.activityIds.includes(activity.activityId)} />
                </ListItemIcon>
                <ListItemText primary={activity.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant='outlined' size='small'>
          <InputLabel shrink>Role applications</InputLabel>
          <Select
            multiple
            displayEmpty
            value={value.activityRoleIds}
            onChange={(e) => onChange({ ...value, activityRoleIds: e.target.value as string[] })}
            input={<OutlinedInput notched label='Role applications' />}
            renderValue={(selected) =>
              (selected as string[]).length === 0 ? 'No roles' : namesFor(selected as string[])
            }
          >
            {roles.map((role) => (
              <MenuItem key={role.activityRoleId} value={role.activityRoleId}>
                <ListItemIcon>
                  <Checkbox color='primary' checked={value.activityRoleIds.includes(role.activityRoleId)} />
                </ListItemIcon>
                <ListItemText primary={role.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};
